import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize, withUiScale } from "./uiTokens.ts";

/**
 * OperatorUnlockBanner — WOW Pass 2 (S190 item 2): the progression PAYOFF when a
 * new AI model comes online. The unlock itself already happens silently in
 * `runEndHandler` (a push to `metaState.unlockedOperators` at the WARDEN 3 /
 * GHOST 5 / ARCHITECT 8 / CIPHER 12 thresholds); this is the celebratory beat
 * that announces it — "NEW AI DECRYPTED · PHANTOM · NOW DEPLOYABLE" — landing
 * over the run-end screen and tying back to the just-shipped pre-run carousel
 * (where the formerly-locked card is now browsable + deployable).
 *
 * READ-ONLY: it never touches MetaState or unlock thresholds; main.ts detects
 * the freshly-unlocked operator by diffing the unlocked set across the run and
 * passes the display name(s) here. Layer 4 (DOM/CSS).
 *
 * Reduced-motion-safe: the entrance/glow are CSS-keyframed and gated OFF under
 * `prefers-reduced-motion` (the banner resolves to its static rest state); the
 * auto-dismiss timer is the only behaviour that always runs. Decorative
 * (pointer-events:none) so it never steals the run-end CTAs.
 */
export class OperatorUnlockBanner {
  private overlay: HTMLDivElement;
  private timer: number | null = null;

  /** How long the celebration holds before it fades itself out. */
  private static readonly HOLD_MS = 3400;

  constructor(parent: HTMLElement) {
    this.overlay = document.createElement("div");
    this.overlay.id = "operator-unlock-banner";
    // Polite live-region: a screen reader hears the unlock even though the
    // visual beat is decorative (mirrors GameBanner's announce() choke point).
    this.overlay.setAttribute("role", "status");
    this.overlay.setAttribute("aria-live", "polite");
    this.injectStyles();
    parent.appendChild(this.overlay);
    this.overlay.style.display = "none";
  }

  /**
   * Celebrate one or more freshly-unlocked operators (already-resolved display
   * names, e.g. "PHANTOM"). No-op on an empty list. Re-showing restarts the
   * hold timer (the latest unlock wins).
   */
  show(operatorNames: string[]): void {
    if (operatorNames.length === 0) return;
    if (this.timer !== null) clearTimeout(this.timer);

    const names = operatorNames.join(" · ");
    this.overlay.innerHTML = `
      <div class="oub-card">
        <div class="oub-kicker">${t("unlock.newAi")}</div>
        <div class="oub-name">${names}</div>
        <div class="oub-sub">${t("unlock.deployable")}</div>
      </div>
    `;
    this.overlay.style.display = "flex";
    // Re-trigger the entrance animation on a re-show (force reflow).
    this.overlay.classList.remove("oub-enter");
    void this.overlay.offsetWidth;
    this.overlay.classList.add("oub-enter");

    this.timer = window.setTimeout(() => this.hide(), OperatorUnlockBanner.HOLD_MS);
  }

  hide(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.overlay.style.display = "none";
    this.overlay.classList.remove("oub-enter");
  }

  private injectStyles(): void {
    if (document.getElementById("operator-unlock-styles")) return;
    const style = document.createElement("style");
    style.id = "operator-unlock-styles";
    style.textContent = `
      #operator-unlock-banner {
        position: absolute; inset: 0; display: flex;
        align-items: flex-start; justify-content: center;
        z-index: ${UI_Z.OVERLAY}; pointer-events: none;
        font-family: ${UI_FONT_STACK};
      }
      .oub-card {
        margin-top: 14%;
        text-align: center;
        padding: 14px 30px;
        background: linear-gradient(180deg, rgba(8,12,24,0.86), rgba(8,12,24,0.7));
        border: 1px solid ${COLORS.AMBER_WARN};
        box-shadow: 0 0 22px rgba(232,168,51,0.4), inset 0 0 14px rgba(8,12,24,0.6);
      }
      .oub-kicker {
        font-size: ${roleFontSize("caption")}; color: ${COLORS.AMBER_WARN};
        letter-spacing: 6px; opacity: 0.9;
        text-shadow: 0 0 10px ${COLORS.AMBER_WARN};
      }
      .oub-name {
        font-size: ${withUiScale(30)}; color: ${COLORS.SENTINEL_GREEN};
        letter-spacing: 8px; margin: 6px 0 4px; font-weight: bold;
        text-shadow: 0 0 16px ${COLORS.SENTINEL_GREEN}, 0 0 30px ${COLORS.SENTINEL_GREEN};
      }
      .oub-sub {
        font-size: ${roleFontSize("micro")}; color: ${COLORS.ICE_CYAN};
        letter-spacing: 4px; opacity: 0.85;
      }
      /* The flourish: a brief decrypt-in + a settling glow pulse on the name. */
      .oub-enter .oub-card { animation: oub-in 0.5s ease-out both; }
      .oub-enter .oub-name { animation: oub-glow 1.6s ease-in-out 0.5s infinite alternate; }
      @keyframes oub-in {
        0% { opacity: 0; transform: translateY(-12px) scale(0.96); filter: brightness(2.4); }
        100% { opacity: 1; transform: translateY(0) scale(1); filter: brightness(1); }
      }
      @keyframes oub-glow {
        0% { text-shadow: 0 0 10px ${COLORS.SENTINEL_GREEN}; }
        100% { text-shadow: 0 0 18px ${COLORS.SENTINEL_GREEN}, 0 0 34px ${COLORS.SENTINEL_GREEN}; }
      }
      /* Reduced motion: no decrypt-in, no glow pulse — the static lit card only. */
      @media (prefers-reduced-motion: reduce) {
        .oub-enter .oub-card, .oub-enter .oub-name { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }
}
